import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import useGeneralRequestProperties from 'components/Dashboard/hooks/useGeneralRequestProperties';
import useCreateParliamentLiveFilters from 'components/Dashboard/ParliamentLive/useCreateParliamentLiveFilters';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
import { useContext, useEffect, useRef, useState } from 'react';
import { store } from 'components/Store';

const DEFAULT_INTERVAL_MS = 30000;

const useIntervalCreateResults = (props) => {
  const {
    createResults,
    callCondition,
    currentlyProcessing,
    contentFilters,
    noIntervalCall = false,
    setFreshItemsCount,
    refreshTrigger,
  } = props ?? {};

  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists } = state;
  const sectionName = createSectionName();
  const { inboxSection } = useMainSectionParameter();

  const { createRequestProperties } = useGeneralRequestProperties({ sectionName });
  const { createParliamentLiveFilters } = useCreateParliamentLiveFilters();
  const { initialCallSecondDateValue } = createParliamentLiveFilters({});

  const topicParam = getUrlParam('topic-id');
  const searchParam = getUrlParam('search') ?? getUrlParam('or');

  const minutesPerCallParam = getUrlParam('minutesPerCall');
  const minutesPerCall = minutesPerCallParam ?? DEFAULT_INTERVAL_MS / 60000;
  const interval = DEFAULT_INTERVAL_MS;

  const [date, setDate] = useState(initialCallSecondDateValue);
  const resetDateRef = useRef(initialCallSecondDateValue);
  const intervalCallInProgressRef = useRef(false);
  // Always keep the latest "initialCallSecondDateValue" in a ref so interval cleanup
  // doesn't accidentally reset to a stale mount-time value.
  resetDateRef.current = initialCallSecondDateValue;

  const isParliamentLive = sectionName === 'ParliamentLive';
  const shouldRunInterval =
    inboxSection && !isParliamentLive && callCondition && currentlyProcessing?.state === true && !noIntervalCall;

  const dateField = 'addedToFeedUtc';

  const intervalCallRef = useRef(null);
  intervalCallRef.current = async (source) => {
    if (intervalCallInProgressRef.current) {
      return;
    }
    intervalCallInProgressRef.current = true;
    try {
      const secondDateValue = dayjs.utc(date).add(minutesPerCall, 'minute').format('YYYY-MM-DDTHH:mm:ss') + 'Z';

      const directTopic = keywordsLists?.find((item) =>
        topicParam === 'all' ? item?.id === null : item?.id === parseInt(topicParam)
      );
      const { requestProperties } = createRequestProperties({
        pageNumber: 1,
        noUseQuery: false,
        customPageSize: 10000,
        directTopic,
      });

      const dateValueUtc = date?.endsWith('Z') ? date : dayjs.utc(date).format('YYYY-MM-DDTHH:mm:ss') + 'Z';
      const dateFilters = [
        { field: dateField, value: dateValueUtc, operator: 'DATE_GTE' },
        { field: dateField, value: secondDateValue, operator: 'date_lt' },
      ];
      const newFilters = [
        ...(requestProperties?.filters ?? []).filter((item) => item?.field !== dateField),
        ...dateFilters,
      ];
      const newRequestProperties = { ...requestProperties, filters: newFilters };

      const stateResults = await createResults({
        pageNumber: 1,
        setMarker: false,
        source,
        notForceLoading: true,
        requestProperties: newRequestProperties,
        fromInterval: true,
        returnOnlyForInterval: true,
      });
      setDate(secondDateValue);
      if (stateResults && setFreshItemsCount) {
        setFreshItemsCount((prev) => prev + (stateResults[sectionName]?.totalHits ?? 0));
      }
    } catch (error) {
    } finally {
      intervalCallInProgressRef.current = false;
    }
  };

  useEffect(() => {
    if (!shouldRunInterval) return;

    const source = new AbortController();
    if (setFreshItemsCount) {
      setFreshItemsCount(0);
    }
    const id = setInterval(() => intervalCallRef.current(source), interval);
    return () => {
      source.abort('Interval createResults canceled by the user.');
      clearInterval(id);
      setDate(resetDateRef.current);
      intervalCallInProgressRef.current = false;
    };
  }, [
    sectionName,
    topicParam,
    searchParam,
    contentFilters,
    interval,
    currentlyProcessing?.state,
    callCondition,
    noIntervalCall,
    shouldRunInterval,
    inboxSection,
    refreshTrigger,
  ]);
};

export default useIntervalCreateResults;
